import './common/assets/scss/App.scss'
import { type ReactElement, useEffect, useState } from 'react'
import { Outlet, useLocation } from 'react-router'
import LoadingScreen from './common/components/LoadingScreen'
import { Alert } from 'react-bootstrap'
import { t } from 'i18next'
import useAppLayout, { LayoutAction } from './common/hooks/useAppLayout'
import { ErrorBoundary } from 'react-error-boundary'
import ErrorFallback from './errors/ErrorFallback'
import { hasAuthParams, useAuth } from 'react-oidc-context'
import { useSessionStorage } from 'usehooks-ts'
import ToastNotificationContextProvider from './common/components/ToastNotificationContextProvider'

function App (): ReactElement {
  const auth = useAuth()
  const location = useLocation()
  const [authError, setAuthError] = useState<boolean>(false)
  const { dispatch } = useAppLayout()

  const activeSignIn = !!auth.activeNavigator

  const [hashCache, setHashCache] = useSessionStorage<string>(
    'login_hash_cache',
    ''
  )

  // Based on the 'react-oidc-context' docs example:
  // https://github.com/authts/react-oidc-context#automatic-sign-in
  useEffect(() => {
    if (authError) return

    if (
      !hasAuthParams() &&
      !auth.isAuthenticated &&
      !activeSignIn &&
      !auth.isLoading
    ) {
      setHashCache(window.location.hash)

      auth.signinRedirect().catch(() => {
        setAuthError(true)
      })
    }
  }, [activeSignIn, auth, authError, setHashCache])

  useEffect(() => {
    if (!auth.isAuthenticated) {
      return
    }

    dispatch({ type: LayoutAction.CLOSE_ALL_MENUS })
  }, [auth.isAuthenticated, dispatch, location.pathname])

  if (authError) {
    return (
      <LoadingScreen error={true}>
        <Alert variant="danger">{t('authentication.error')}</Alert>
      </LoadingScreen>
    )
  }

  if (!auth.isAuthenticated || activeSignIn) {
    return <LoadingScreen />
  }

  if (hashCache.length > 0 && window.location.hash.length === 0) {
    window.location.hash = hashCache
    setHashCache('')
  }

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <div className="app-wrapper">
        <ToastNotificationContextProvider>
          <main className="d-flex flex-row">
            <div className="content w-100">
              <Outlet />
            </div>
          </main>
        </ToastNotificationContextProvider>
      </div>
    </ErrorBoundary>
  )
}

export default App
